import { createReducer } from '@reduxjs/toolkit'
import { AllowedRefs, convertToRefString } from 'lib/websocket-notify'
import * as act from './multigetActions'
import { SerializedErrorType } from './reducer'

// ---------- Types ----------
export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'stale'

export interface MultiGetSlice<T = unknown> {
    byId: Record<string, T | undefined>
    status: Record<string, LoadStatus>
    error: Record<string, SerializedErrorType | undefined>
    lastFetched: Record<string, number>
}

export type MultiGetRootState<T = unknown> = Record<string, MultiGetSlice<T>>

const createInitialSlice = <T>(): MultiGetSlice<T> => ({
    byId: {},
    status: {},
    error: {},
    lastFetched: {},
})

// ---------- Reducer ----------

export const multigetSliceReducer = createReducer({} as MultiGetRootState, (builder) => {
    // queued / requested share same logic -> set status to loading
    const handleRefs = (
        state: MultiGetRootState,
        payload: { type: string; refs: AllowedRefs[] },
        toStatus: LoadStatus,
    ): void => {
        const slice = state[payload.type] ?? (state[payload.type] = createInitialSlice())
        // Extract tag from cacheKey format "{tag}:{serviceName}.{methodName}"
        const tag = payload.type.split(':')[0]
        for (const ref of payload.refs) {
            const key = convertToRefString(ref, tag)
            if (!key) continue
            slice.status[key] = toStatus
        }
    }

    builder
        .addCase(act.queued, (state, { payload }) => {
            handleRefs(state, payload, 'loading')
        })
        .addCase(act.requested, (state, { payload }) => {
            handleRefs(state, payload, 'loading')
        })
        .addCase(act.fulfilled, (state, { payload }) => {
            const slice = state[payload.type] ?? (state[payload.type] = createInitialSlice())
            const now = Date.now()
            for (const [key, obj] of Object.entries(payload.objects)) {
                slice.byId[key] = obj
                slice.status[key] = 'ready'
                slice.error[key] = undefined
                slice.lastFetched[key] = now
            }
        })
        .addCase(act.rejected, (state, { payload }) => {
            const slice = state[payload.type] ?? (state[payload.type] = createInitialSlice())
            for (const [key, err] of Object.entries(payload.errors)) {
                slice.status[key] = 'error'
                slice.error[key] = err
            }
        })
        .addCase(act.markStale, (state, { payload }) => {
            // Cache keys have format "{tag}:{serviceName}.{methodName}"
            // If type contains ":", it's a full cacheKey - use directly
            // If type doesn't contain ":", it's a tag - find all matching slices
            const isFullCacheKey = payload.type.includes(':')
            const tag = isFullCacheKey ? payload.type.split(':')[0] : payload.type
            const sliceKeys = isFullCacheKey
                ? [payload.type]
                : Object.keys(state).filter((k) => k.startsWith(`${payload.type}:`))

            const key = convertToRefString(payload.ref, tag)
            if (!key) return

            for (const sliceKey of sliceKeys) {
                const slice = state[sliceKey]
                if (slice && (slice.status[key] === 'ready' || slice.status[key] === 'error')) {
                    slice.status[key] = 'stale'
                }
            }
        })
        .addCase(act.evict, (state, { payload }) => {
            const slice = state[payload.type]
            if (!slice) return
            const cutoff = payload.cutoff
            for (const [key, ts] of Object.entries(slice.lastFetched)) {
                if (ts < cutoff) {
                    delete slice.byId[key]
                    delete slice.status[key]
                    delete slice.error[key]
                    delete slice.lastFetched[key]
                }
            }
        })
})
